import { TableRowWrapper } from 'components/common/table/TableRow';
import { observer } from 'mobx-react-lite';
import moment from 'moment';
import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { __RouterContext } from 'react-router';
import { DATE_FORMAT_SHORTER } from '../../../config';
import { DEBTOR, DebtorRouteParams } from '../../../routes';
import { Case } from '../../../server-api/model';
import { actionsContext } from '../../../state/actionsState';
import { CaseSorter } from '../../../state/caseSorter';
import { caseContext } from '../../../state/caseState';
import { checkCazeClosed } from '../../../state/search/utils';
import { formatNumber } from '../../../utils/formatNumber';
import {
  useDetectMobileOverflow,
  useHorizontalDragScroll,
  useIsSmallViewport,
} from '../../../utils/hooks';
import { SortHeaderCell } from '../../common/table/SortHeaderCell';
import { CaseExpansion } from './CaseExpansion';

interface InfoCaseTableProps {
  entries: Case[];
  sorter: CaseSorter;
}
export const InfoCaseTable: FC<InfoCaseTableProps> = observer(
  ({ entries, sorter }) => {
    const actionsState = useContext(actionsContext);
    const routerState = useContext(__RouterContext);
    const caseState = useContext(caseContext);
    const tableRef = useRef<HTMLTableElement>(null);
    const cazeIdParam = (routerState.match.params as DebtorRouteParams).caze;
    useHorizontalDragScroll(tableRef);
    useDetectMobileOverflow(tableRef);
    const isSmallViewport = useIsSmallViewport();

    const cellsHeaders: Record<string, string> = {
      caseRef: 'Case Ref',
      contract: 'Contract',
      warrant: 'Warrant/LO date',
      originalDebt: 'Amount',
      stage: 'Stage',
      vrm: 'VRM',
      hold: 'Hold',
      status: 'Status',
      balance: 'Balance'
    };

    const jsxEntries = useMemo(() => {
      return entries.map((caze) => {
        const cellsToShow = {
          caseRef: caze.clientReference,
          contract: caze.contract.name,
          warrant:
            caze.warrant && caze.warrant.warrantDate
              ? moment(caze.warrant.warrantDate).format(DATE_FORMAT_SHORTER)
              : '',
          originalDebt: '£' + (formatNumber(caze.debtOnly) || '0.00'),
          stage: checkCazeClosed(caze.status)
            ? ''
            : caze.lastStage && caze.lastStage.name
              ? caze.lastStage.name
              : '',
          vrm: caze.originalVrm ? caze.originalVrm : '',
          hold: caze.isOnHold ? 'Yes' : 'No',
          status: caze.status
            ? (
              caze.status.charAt(0).toUpperCase() +
              caze.status.slice(1).toLowerCase()
            ).replace(/_/g, ' ')
            : '',
          balance: '£' + formatNumber(caze.debtBalance),
        };

        const link = DEBTOR + '/' + caze.debtor.id + '/cases/' + caze.id;

        return (
          <TableRowWrapper
            cellsToShow={cellsToShow}
            cellsHeaders={cellsHeaders}
            bookmarks={true}
            key={'row-' + caze.id}
            item={caze}
            selectionPool={actionsState.selectedCases}
            link={link}
            expansion={<CaseExpansion caze={caze} />}
            compactViewMode={isSmallViewport}
            colSpan={12}
          />
        );
      });
    }, [entries, actionsState.selectedCases, isSmallViewport]);

    useEffect(() => {
      caseState.focusCase(cazeIdParam || null);
    }, [cazeIdParam]);

    useEffect(() => {
      caseState.setScrolledToCase(false);
    }, []);

    return isSmallViewport ? <div className='StandardTable'>{jsxEntries}</div> :
      (
        <table className='StandardTable expandable' ref={tableRef} aria-label='Cases'>
          <thead>
            <tr className='header'>
              <>
                <th className='cell-chevron'>
                  <span className='hidden-header'>Expand</span>
                </th>
                <th className='cell-bookmark'>
                  <span className='hidden-header'>Bookmark</span>
                </th>
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  type='clientReference'
                  className='cell-caseref'
                  text={cellsHeaders.caseRef}
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  type='contract'
                  className='cell-contract'
                  text={cellsHeaders.contract}
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  type='warrantDate'
                  className='cell-warrant'
                  text={cellsHeaders.warrant}
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  type='debtOnly'
                  className='cell-originaldebt'
                  text={cellsHeaders.originalDebt}
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  type='stage'
                  text={cellsHeaders.stage}
                  className='cell-stage'
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  type='originalVrm'
                  text={cellsHeaders.vrm}
                  className='cell-vrm'
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  className='cell-hold'
                  type='isOnHold'
                  text={cellsHeaders.hold}
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  className='cell-status'
                  type='status'
                  text={cellsHeaders.status}
                />
                <SortHeaderCell
                  mode='td'
                  sorter={sorter}
                  className='cell-balance'
                  type='debtBalance'
                  text={cellsHeaders.balance}
                />
                <th className='cell-checkbox'>
                  <span className='hidden-header'>Select</span>
                </th>
              </>
            </tr>
          </thead>
          <tbody className='table-body'>
            {jsxEntries}
          </tbody>
        </table>
      );
  }
);
